interface ExperienceGroup {
  name: string;
  bullets: string[];
}

interface ExperienceEntry {
  dateRange: string;
  dateLabel: string;
  role: string;
  company: string;
  companyLink?: string;
  location: string;
  groups: ExperienceGroup[];
  links: { name: string; href: string }[];
  tech: string[];
}

const ArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className="inline-block h-4 w-4 shrink-0 transition-transform group-hover/link:-translate-y-1 group-hover/link:translate-x-1 group-focus-visible/link:-translate-y-1 group-focus-visible/link:translate-x-1 motion-reduce:transition-none ml-1 translate-y-px"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z"
      clipRule="evenodd"
    ></path>
  </svg>
);

const LinkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className="mr-1 h-3 w-3"
    aria-hidden="true"
  >
    <path d="M12.232 4.232a2.5 2.5 0 013.536 3.536l-1.225 1.224a.75.75 0 001.061 1.06l1.224-1.224a4 4 0 00-5.656-5.656l-3 3a4 4 0 00.225 5.865.75.75 0 00.977-1.138 2.5 2.5 0 01-.142-3.667l3-3z"></path>
    <path d="M11.603 7.963a.75.75 0 00-.977 1.138 2.5 2.5 0 01.142 3.667l-3 3a2.5 2.5 0 01-3.536-3.536l1.225-1.224a.75.75 0 00-1.061-1.06l-1.224 1.224a4 4 0 105.656 5.656l3-3a4 4 0 00-.225-5.865z"></path>
  </svg>
);

const EXPERIENCE: ExperienceEntry[] = [
  {
    dateRange: "09/2024 — Present",
    dateLabel: "September 2024 to Present",
    role: "Software Engineer II",
    company: "Trajector",
    companyLink: "https://www.trajector.com/",
    location: "Gurugram, India",
    groups: [
      {
        name: "Nexus",
        bullets: [
          "Architected and owned the Disability Decision Tool, enabling a case-management workflow adopted by 90% of users.",
          "Developed a MongoDB-backed rating engine replacing an external API, reducing latency by 500ms while eliminating vendor costs.",
          "Removed four legacy applications (33K+ LOC), lowering maintenance overhead and build time.",
          "Implemented Cloudflare Zero Trust authentication and remediated 6+ production CVEs.",
        ],
      },
      {
        name: "Client Portals",
        bullets: [
          "Integrated Amazon Cognito authentication for secure user onboarding.",
          "Implemented an event publishing service to track user-journey and analytics using AWS EventBridge.",
          "Increased frontend test coverage from 10% to 94% using Vitest.",
        ],
      },
    ],
    links: [],
    tech: [
      "TypeScript",
      "React",
      "Node.js",
      "MongoDB",
      "AWS EventBridge",
      "Amazon Cognito",
      "Cloudflare",
    ],
  },
  {
    dateRange: "02/2022 — 09/2024",
    dateLabel: "February 2022 to September 2024",
    role: "Software Development Engineer II",
    company: "Strategywerks",
    companyLink: "https://strategywerks.ai/",
    location: "Gurugram, India",
    groups: [
      {
        name: "Mysterious Messenger",
        bullets: [
          "Introduced a content moderation pipeline for text and images using AWS Rekognition.",
          "Implemented deep linking using Branch.io, enabling seamless onboarding for thousands of users.",
          "Built real-time messaging using WebSockets and Firebase Push Notifications, improving user engagement by 30–40%.",
          "Designed and delivered games, polls, and media upload features used daily by thousands of users.",
        ],
      },
      {
        name: "Baby Diary",
        bullets: [
          "Built a microservices-based media processing pipeline using FFmpeg for automated moment creation.",
          "Built a video processing pipeline using AWS MediaConvert to generate different HLS quality formats, supporting 10K+ videos.",
        ],
      },
    ],
    links: [
      {
        name: "Mysterious",
        href: "https://play.google.com/store/apps/details?id=com.mysterioussocialandroid&hl=en_IN&pli=1",
      },
    ],
    tech: [
      "React Native",
      "Node.js",
      "WebSockets",
      "Firebase",
      "AWS Rekognition",
      "AWS MediaConvert",
      "FFmpeg",
      "Branch.io",
    ],
  },
  {
    dateRange: "06/2020 — 09/2021",
    dateLabel: "June 2020 to September 2021",
    role: "Software Engineer",
    company: "Click Labs",
    companyLink: "https://jungleworks.com/",
    location: "Chandigarh, India",
    groups: [
      {
        name: "Yelo",
        bullets: [
          "Improved SEO using Angular SSR and reduced bundle size by 76% (1.3 MB to 306 KB).",
          "Enhanced Google Maps Geofencing for service-area enforcement.",
          "Revamped the product's UI/UX from scratch, simplifying user workflows and delivering a modern, intuitive experience.",
        ],
      },
    ],
    links: [{ name: "Yelo", href: "https://jungleworks.com/yelo/" }],
    tech: ["Angular", "SCSS", "JavaScript", "Google Maps API"],
  },
];

const Experience = () => {
  return (
    <section
      id="experience"
      className="mb-16 scroll-mt-16 md:mb-24 lg:mb-36 lg:scroll-mt-24"
      aria-label="Work experience"
    >
      <div className="sticky top-0 z-20 -mx-6 mb-4 w-screen bg-slate-900/75 px-6 py-5 backdrop-blur md:-mx-12 md:px-12 lg:sr-only lg:relative lg:top-auto lg:mx-auto lg:w-full lg:px-0 lg:py-0 lg:opacity-0">
        <h2 className="text-sm font-bold uppercase tracking-widest text-slate-200 lg:sr-only">
          Experience
        </h2>
      </div>
      <div>
        <ol className="group/list">
          {EXPERIENCE.map((entry) => (
            <li className="mb-12" key={entry.company}>
              <div className="group relative grid pb-1 transition-all sm:grid-cols-8 sm:gap-8 md:gap-4 lg:hover:!opacity-100 lg:group-hover/list:opacity-50">
                <div className="absolute -inset-x-4 -inset-y-4 z-0 hidden rounded-xl transition motion-reduce:transition-none lg:-inset-x-6 lg:block lg:group-hover:bg-gradient-to-br lg:group-hover:from-teal-400/10 lg:group-hover:via-slate-800/40 lg:group-hover:to-fuchsia-400/10 lg:group-hover:shadow-[0_0_40px_-12px_rgba(45,212,191,0.35)] lg:group-hover:ring-1 lg:group-hover:ring-inset lg:group-hover:ring-white/10 lg:group-hover:backdrop-blur-sm"></div>
                <header
                  className="z-10 mb-2 mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:col-span-2"
                  aria-label={entry.dateLabel}
                >
                  {entry.dateRange}
                </header>
                <div className="z-10 sm:col-span-6">
                  <h3 className="font-display font-medium leading-snug text-slate-200">
                    <div>
                      {entry.companyLink ? (
                        <a
                          className="inline-flex items-baseline font-medium leading-tight text-slate-200 hover:text-teal-300 focus-visible:text-teal-300 group/link text-base"
                          href={entry.companyLink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <span className="absolute -inset-x-4 -inset-y-2.5 hidden rounded md:-inset-x-6 md:-inset-y-4 lg:block"></span>
                          <span>
                            {entry.role} ·{" "}
                            <span className="inline-block">
                              {entry.company}
                              <ArrowIcon />
                            </span>
                          </span>
                        </a>
                      ) : (
                        <span className="inline-flex items-baseline font-medium leading-tight text-slate-200 text-base">
                          {entry.role} · {entry.company}
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="text-slate-500" aria-hidden="true">
                        {entry.location}
                      </div>
                    </div>
                  </h3>

                  <div className="mt-3 space-y-3">
                    {entry.groups.map((group) => (
                      <div key={group.name}>
                        {entry.groups.length > 1 && (
                          <h4 className="text-sm font-semibold text-fuchsia-300/80">
                            {group.name}
                          </h4>
                        )}
                        <ul className="mt-1 list-disc space-y-1.5 pl-4 text-sm leading-normal text-slate-400">
                          {group.bullets.map((bullet, i) => (
                            <li key={i}>{bullet}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                  {entry.links.length > 0 && (
                    <ul
                      className="mt-2 flex flex-wrap"
                      aria-label="Related links"
                    >
                      {entry.links.map((link) => (
                        <li className="mr-4" key={link.name}>
                          <a
                            className="relative mt-2 inline-flex items-center text-sm font-medium text-slate-300 hover:text-teal-300 focus-visible:text-teal-300"
                            href={link.href}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <LinkIcon />
                            <span>{link.name}</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}

                  <ul
                    className="mt-2 flex flex-wrap"
                    aria-label="Technologies used"
                  >
                    {entry.tech.map((tech) => (
                      <li className="mr-1.5 mt-2" key={tech}>
                        <div className="flex items-center rounded-full bg-gradient-to-r from-teal-400/10 to-fuchsia-400/10 px-3 py-1 text-xs font-medium leading-5 text-teal-200 ring-1 ring-inset ring-teal-400/20 backdrop-blur-sm transition hover:ring-teal-300/40">
                          {tech}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default Experience;
